import { gql } from "@apollo/client";
import DataTableLabel from "controls/DataTableLabel";
import Markdown from "controls/Markdown";
import Message from "controls/Message";
import Tabs from "controls/Tabs";
import getUiString from "helpers/getUiString";
import useData from "hooks/useData";
import React, { cloneElement } from "react";
import { useParams } from "react-router-dom";
import ReadminPage from "readmin_pages/ReadminPage";
import { underscored } from "underscore.string";

import ReadminAssociationTableTab from "./ReadminAssociationTableTab";
import ReadminDetailsTab from "./ReadminDetailsTab";
import ReadminObjectDialogToolbar from "./ReadminObjectDialogToolbar";
import ReadminObjectLabelDescriptions, {
  ReadminObjectLabelDescriptionsFragment,
} from "./ReadminObjectLabelDescriptions";
import ReadminObjectLabels, { ReadminObjectLabelsFragment } from "./ReadminObjectLabels";
import ReadminObjectThumbnail from "./ReadminObjectThumbnail";

export function makeReadminObjectPage(typename, paramName, { tabs = [], prependTabs = [], noDetailsTab = false } = {}) {
  return function () {
    return <ReadminObjectPage {...{ typename, paramName, tabs, prependTabs, noDetailsTab }} />;
  };
}

function ReadminObjectPage({ typename, paramName, prependTabs, noDetailsTab, tabs, readminObject }) {
  let { orgId, apiPartnerId, [paramName]: id } = useParams();

  const [data, dataMeta] = useData(
    gql`
      query ReadminObjectPage($typename: String!, $id: ID!) {
        readminObject(typename: $typename, id: $id) {
          id
          readminName
          readminLogoUrl
          ...ReadminObjectLabelsFragment
          ...ReadminObjectLabelDescriptionsFragment
        }
      }
      ${ReadminObjectLabelDescriptionsFragment}
      ${ReadminObjectLabelsFragment}
    `,
    {
      typename,
      id,
    },
  );

  if (paramName === "apiPartnerId") apiPartnerId = id;
  if (paramName === "orgId") orgId = id;

  const typeSingular = getUiString(`readmin.${underscored(typename)}.singular`);

  return (
    <ReadminPage
      logoUrl={data?.readminObject?.readminLogoUrl}
      title={data?.readminObject?.readminName}
      toolbar={<ReadminObjectDialogToolbar id={id} typename={typename} gotoReadminObjectUrl />}
      subheader={<Markdown uiStringKey={`readmin.${underscored(typename)}.description`} />}
      labels={
        <>
          <DataTableLabel label={typeSingular} />
          <ReadminObjectLabels
            readminObject={data?.readminObject}
            onSubmit={async () => {
              if (data) await dataMeta.refetch();
            }}
          />
        </>
      }
      thumbnail={<ReadminObjectThumbnail id={id} typename={typename} />}
      orgId={orgId}
      apiPartnerId={apiPartnerId}
    >
      {data && !data.readminObject ? (
        <Message
          type="error"
          title="Record Not Found"
          content={`Could not find this ${typeSingular} record. It may have been deleted.`}
        />
      ) : (
        <>
          <ReadminObjectLabelDescriptions readminObject={readminObject} />
          <Tabs
            tabs={[
              ...prependTabs.map(([name, title, component]) => [
                name,
                title,
                cloneElement(component, { [paramName]: id, id, typename }),
              ]),
              ...[
                //
                [
                  "details",
                  "Details",
                  <ReadminDetailsTab
                    onSubmit={async () => {
                      if (data) await dataMeta.refetch();
                    }}
                  />,
                  { hidden: noDetailsTab },
                ],
                ...tabs,
                ["notes", "System Notes", <ReadminAssociationTableTab associationName="notes" />],
              ].map(([name, title, component, options]) => [
                name,
                title,
                cloneElement(component, { [paramName]: id, id, typename }),
                options,
              ]),
            ]}
          />
        </>
      )}
    </ReadminPage>
  );
}
